import { Check, X, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const GET = [
  "A permanent, numbered entry in the private HYLUNER registry",
  "A unique Registry ID and Certificate ID",
  "Celestial coordinate information (selenographic for Moon, ICRS/J2000 for stars)",
  "A 2D visualisation of the referenced location",
  "A downloadable PDF certificate and an on-screen certificate page",
  "A QR code resolving to a live public verification page",
  "A public registry page you can share",
  "A customer dashboard to manage your records",
];

const NOT = [
  "Legal ownership of the Moon, lunar land, stars or any celestial body",
  "Government-recognised title, deed or property rights",
  "Official astronomical naming — the IAU is the only body that names celestial objects",
  "Exclusive rights to a star or lunar area recognised by any state or authority",
  "Any interest that can be sold, inherited or registered as real property",
  "Recognition by NASA, ESA, the IAU or any government agency",
];

export default function Transparency() {
  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-10 py-24" data-testid="transparency-page">
      <div className="hy-label">Transparency Statement</div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-5">What HYLUNER is —<br />and what it is not</h1>
      <p className="font-serif-accent italic text-lg text-slate-300 mt-8 max-w-3xl leading-relaxed">
        Transparency and verification are the entire point of this registry. Below is a plain statement of
        what a HYLUNER registration provides, written before you pay rather than buried afterwards.
      </p>

      <div className="hy-glass p-8 mt-14 flex gap-5" data-testid="legal-position">
        <ShieldCheck className="w-6 h-6 shrink-0" style={{ color: "var(--hy-gold)" }} strokeWidth={1.4} />
        <p className="text-sm text-slate-300 leading-relaxed">
          HYLUNER operates a <span className="text-amber-200">private commemorative registry and dedication
          experience</span>. Under the 1967 Outer Space Treaty, celestial bodies are not subject to national
          appropriation, and no company — including HYLUNER — can sell ownership of them. We therefore make no
          ownership claim of any kind. We record dedications, and we make those records verifiable.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mt-16" id="what-you-get">
        <div className="hy-card p-8" data-testid="what-you-get">
          <div className="hy-label text-emerald-300/80">What you receive</div>
          <ul className="space-y-4 mt-7">
            {GET.map((t) => (
              <li key={t} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="hy-card p-8" data-testid="what-you-dont-get">
          <div className="hy-label text-red-300/80">What you do NOT receive</div>
          <ul className="space-y-4 mt-7">
            {NOT.map((t) => (
              <li key={t} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
                <X className="w-4 h-4 mt-0.5 shrink-0 text-red-400" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-20 space-y-10">
        <Block title="On registry numbers">
          A HYLUNER registry number identifies the position of a record in our own sequence. Registry #001
          means “the first HYLUNER registry record” — nothing more. It is not an astronomical rank, brightness
          order, or any statement about the star or lunar area itself.
        </Block>
        <Block title="On coordinates">
          Coordinates are real reference-frame coordinates used to identify a position in the sky or on the
          lunar surface. They describe where your dedication points. They do not encumber, reserve or allocate
          that position in any legal or scientific sense.
        </Block>
        <Block title="On verification">
          Every paid record is published to the public registry with a permanent Registry ID. Anyone can enter
          that ID — or scan the certificate QR code — and see the record as we hold it, including this same
          disclosure.
        </Block>
      </div>

      <Link to="/create" className="hy-btn inline-block px-9 py-4 text-xs uppercase mt-16" data-testid="transparency-create-btn">
        Register a Dedication
      </Link>
    </div>
  );
}

const Block = ({ title, children }) => (
  <div>
    <h2 className="font-display text-xl sm:text-2xl text-amber-200/90">{title}</h2>
    <div className="hy-rule my-5" />
    <p className="text-sm text-slate-400 leading-relaxed max-w-3xl">{children}</p>
  </div>
);
